import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Eraser } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const usePad = (open) => {
  const ref = useRef(null);
  const [empty, setEmpty] = useState(true);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const c = ref.current;
      if (!c) return;
      const r = c.getBoundingClientRect();
      c.width = r.width * 2; c.height = r.height * 2;
      const g = c.getContext("2d");
      g.scale(2, 2); g.lineWidth = 2.4; g.lineCap = "round"; g.lineJoin = "round"; g.strokeStyle = "#0f172a";
      setEmpty(true);
    }, 50);
    return () => clearTimeout(t);
  }, [open]);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture(e.pointerId); const g = ref.current.getContext("2d"); g.beginPath(); g.moveTo(...pos(e)); ref.current.dataset.on = "1"; };
  const move = (e) => { if (ref.current.dataset.on !== "1") return; const g = ref.current.getContext("2d"); g.lineTo(...pos(e)); g.stroke(); setEmpty(false); };
  const up = () => { ref.current.dataset.on = ""; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); };
  return { ref, empty, clear, handlers: { onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerLeave: up } };
};

export const SignatureDialog = ({ ddt, onClose, onSaved }) => {
  const pad = usePad(!!ddt);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNome(ddt?.firma?.nome || ""); }, [ddt]);
  const save = async () => {
    if (pad.empty) return toast.error("Fai firmare il cliente nel riquadro");
    if (!nome.trim()) return toast.error("Scrivi il nome di chi firma");
    setBusy(true);
    try {
      await api.post(`/ddt/${ddt.id}/firma`, { img: pad.ref.current.toDataURL("image/png"), nome });
      toast.success("Firma salvata sul DDT"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ddt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="signature-dialog">
        <DialogHeader><DialogTitle>Firma del destinatario · DDT {ddt?.numero}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Il cliente firma con il dito o con la penna nel riquadro qui sotto.</p>
        <canvas ref={pad.ref} {...pad.handlers} data-testid="signature-canvas" className="h-56 w-full touch-none rounded-xl border-2 border-dashed border-slate-300 bg-white" />
        <Input data-testid="signature-name-input" placeholder="Nome e cognome di chi firma" value={nome} onChange={(e) => setNome(e.target.value)} className="h-11" />
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="signature-clear-button" onClick={pad.clear}><Eraser className="mr-2 h-4 w-4" />Cancella</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" data-testid="signature-save-button" disabled={busy} onClick={save}><PenLine className="mr-2 h-4 w-4" />Salva firma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
